import { type FieldStat } from '@solink/vd-data-layer/api-clients/platform/platform';
import { type EventStandardDefs, type Field } from '@solinkcorp/event-standard';
import { CommonWidgetUtils, type HistogramCustomFieldsDict } from '@solinkcorp/vd-common';
import { type Immutable } from 'immer';
import memoizeOne from 'memoize-one';
import { makeImmerReducer } from './utils';

/**
 * Keep track of what cameras are currently being played in the player.
 */
export interface FieldStatsState {
  fieldStats?: FieldStat[];
  histogramCustomFields: string[];
}

export type FieldStats = Immutable<FieldStat[]>;

export const initialState: FieldStatsState = {
  fieldStats: undefined,
  histogramCustomFields: [],
};

export const fieldStatsReducer = makeImmerReducer(initialState, (draft, action) => {
  switch (action.type) {
    case 'UPDATE_FIELD_STATS':
      draft.fieldStats = action.fieldStats;
      draft.histogramCustomFields = [];
      break;

    case 'UPDATE_HISTOGRAM_CUSTOM_FIELDS': {
      // Only update the `histogramCustomFields` list if `fieldStats` is undefined.
      // Once the "real" fieldStats come in, we ignore histogramCustomFields.
      if (!draft.fieldStats) {
        draft.histogramCustomFields = action.fields;
      }
      break;
    }
  }
});

export const getNoPrefixFieldsDict = memoizeOne(
  (fieldStats: FieldStats): { [fieldName: string]: FieldStat } => {
    const noPrefixFieldsDict: { [fieldName: string]: FieldStat } = {};

    for (const field of fieldStats) {
      const fieldName = field.name.replace('.keyword', '');
      if (!fieldName.includes('.')) {
        noPrefixFieldsDict[fieldName] = field;
      }
    }

    return noPrefixFieldsDict;
  },
);

/**
 * Get derived state from field stats.
 */
export const derivedFieldStatsState = memoizeOne(
  (
    eventStandard: EventStandardDefs,
    fieldStatsState: Immutable<FieldStatsState>,
  ): {
    fieldStatsDict: { [field: string]: FieldStat };
    histogramCustomFieldsDict: HistogramCustomFieldsDict;
    /** Dictionary of fields where field.features.rankBy is true. */
    rankByFieldsDict: { [fieldName: string]: Field };
    /** Dictionary of fields with no prefix. */
    noPrefixFieldsDict: { [fieldName: string]: FieldStat };
  } => {
    const fieldStatsDict: { [field: string]: FieldStat } = {};
    const histogramCustomFieldsDict: HistogramCustomFieldsDict = {};
    const rankByFieldsDict: { [fieldName: string]: Field } = {};

    function processEventField(fieldName: string, checkFieldStats = true) {
      const standardField = eventStandard[fieldName];

      if (!standardField) {
        console.debug(`Cannot find event field ${fieldName} in event-standard`);
      } else if (standardField?.disabled) {
        return;
      } else {
        // This mutates fieldStatsDict and histogramCustomFieldsDict.
        CommonWidgetUtils.processEventField({
          checkFieldStats,
          eventStandard,
          fieldName,
          fieldStats: fieldStatsState.fieldStats,
          fieldStatsDict,
          histogramCustomFieldsDict,
          rankByFieldsDict,
          standardField,
        });
      }
    }

    if (fieldStatsState.fieldStats?.length) {
      for (const field of fieldStatsState.fieldStats) {
        const { name } = field;
        const fieldName = name.replace('.keyword', '');
        fieldStatsDict[fieldName] = field; // since we're already iterate fieldstats, better create a dictionary

        processEventField(fieldName);
      }
    } else if (fieldStatsState.histogramCustomFields) {
      for (const name of fieldStatsState.histogramCustomFields) {
        const fieldName = name.replace('.keyword', '');
        processEventField(fieldName, false);
      }
    }

    return {
      fieldStatsDict,
      histogramCustomFieldsDict,
      rankByFieldsDict,
      noPrefixFieldsDict: fieldStatsState.fieldStats
        ? getNoPrefixFieldsDict(fieldStatsState.fieldStats)
        : {},
    };
  },
);
